<template>
	<div class="instruction-picker">
		<v-autocomplete
			:model-value="modelValue"
			:items="all"
			item-title="name"
			item-value="id"
			:density="density"
			:clearable="true"
			:loading="loading"
			:no-data-text="'No Guided Instructions found.'"
			:disabled="disabled"
			:list-props="{ class: 'pb-0' }"
			auto-select-first
			hide-details
			@update:model-value="onChange">
			<template #label><FieldLabel :label="label ?? 'Guided Instructions (Optional)'" /></template>
			<!-- LSP-20840: mark the org's default template in the dropdown so the person STARTING the
			     action sees which set auto-applies; the customer-facing capture flow never renders
			     this picker, so the marker exists only at selection time. -->
			<template #item="{ item, props: itemProps }">
				<v-list-item v-bind="itemProps">
					<template v-if="item.raw?.isDefault" #append>
						<v-chip v-test="'instruction-picker-default-chip'" size="x-small" color="primary">Default</v-chip>
					</template>
				</v-list-item>
			</template>
			<template #append-item>
				<v-list-item
					v-if="canCreateInstructions && !disabled"
					v-test="'instruction-create-new'"
					class="create-instruction-fixed pa-0"
					@click="openCreate">
					<div class="text-center pt-0 pb-2 w-100">
						<hr class="mb-2" />
						<v-btn class="pa-2 text-none create-instruction-btn" color="transparent">Create New Instructions</v-btn>
					</div>
				</v-list-item>
			</template>
			<template #append-inner>
				<v-icon
					v-if="canCreateInstructions && !disabled"
					v-test="'instruction-create-plus'"
					color="primary"
					@click.stop="openCreate"
					>icon-plus</v-icon
				>
			</template>
		</v-autocomplete>
	</div>
</template>

<script setup lang="ts">
	import { computed, onMounted, onUnmounted, ref, watch } from "vue";
	import { useRouter } from "vue-router";
	import api from "../api";
	import { openInternalRoute } from "@/helpers/openInternalRoute";
	import { useCurrentUserRole } from "@/composables/useCurrentUserRole";
	import type Instruction from "@/types/Instruction";
	import FieldLabel from "@common-ui/components/FieldLabel.vue";

	const props = withDefaults(
		defineProps<{
			modelValue: string | null;
			label?: string;
			disabled?: boolean;
			density?: "default" | "comfortable" | "compact";
			// LSP-16289: when present, "Create New Instructions" navigates in the SAME tab to the
			// editor (instead of opening a new tab) so the caller can detour-and-return. `createReturnTo`
			// is the internal path the editor returns to, `createReturnAction` reopens the right surface,
			// and `createDraftKey` names the sessionStorage slot the caller persists its draft under.
			createReturnTo?: string | null;
			createReturnAction?: string | null;
			createDraftKey?: string | null;
			// LSP-20840: opt-in — after the list loads, preselect the org's default template when
			// the caller hasn't chosen anything yet. One-shot and null-only: it never overwrites an
			// existing selection and never re-fires after the user clears the field. Callers must
			// leave this off while restoring a draft/return context (LSP-16289) so a deliberately
			// cleared selection isn't resurrected.
			preselectDefault?: boolean;
		}>(),
		{
			label: undefined,
			disabled: false,
			density: "compact",
			createReturnTo: null,
			createReturnAction: null,
			createDraftKey: null,
			preselectDefault: false
		}
	);

	const emit = defineEmits<{
		(e: "update:modelValue", value: string | null): void;
		// Fired right before a same-tab create navigation so the caller can persist in-progress state.
		(e: "create-start"): void;
		// LSP-20840 (Bugbot): fired once the preselect attempt has run (applied or not). The
		// one-shot must live in the CALLER, whose state survives a picker remount (e.g. the
		// Request/Video type toggle) — an instance-local flag alone would resurrect a cleared
		// default on remount.
		(e: "preselect-settled"): void;
	}>();

	const router = useRouter();

	// LSP-16036 (rework): the create affordances below route to /instruction/new, which is
	// admin-only (route guard + the now-gated editor). Showing them to non-admins opened a
	// blank/locked editor tab. Gate on `!isLoading && canManageGuidedInstructions` — the
	// loading guard avoids a first-paint flash of the "+" for non-admins before /me resolves.
	const { canManageGuidedInstructions, isLoading: roleLoading } = useCurrentUserRole();
	const canCreateInstructions = computed(() => !roleLoading.value && canManageGuidedInstructions.value);

	const all = ref<Instruction[]>([]);
	const loading = ref(false);
	const selectedInstructionRequests = new Set<string>();
	let preselectAttempted = false;
	// A type toggle can unmount this picker while its load() is still in-flight; the stale
	// continuation must not settle the CALLER's one-shot, or the remounted picker would see the
	// gate already closed and skip a legitimate fresh-open preselect (ls-ai-review).
	let isUnmounted = false;
	onUnmounted(() => {
		isUnmounted = true;
	});

	function getErrorStatus(error: unknown): number | undefined {
		if (error === null || typeof error !== "object") {
			return undefined;
		}
		const errorRecord = error as { status?: unknown; code?: unknown; response?: { status?: unknown } };
		const status = errorRecord.status ?? errorRecord.response?.status ?? errorRecord.code;
		return typeof status === "number" ? status : undefined;
	}

	function isNotFoundError(error: unknown): boolean {
		return getErrorStatus(error) === 404;
	}

	function onChange(next: string | null): void {
		emit("update:modelValue", next ?? null);
	}

	function openCreate(): void {
		// Defense in depth (LSP-16036): the create affordances are hidden for non-admins, but guard
		// the action itself too — mirroring InstructionEditor's create()/update() early-returns — so
		// no programmatic path can route a non-admin into the admin-only /instruction/new editor.
		if (!canCreateInstructions.value) {
			return;
		}
		// LSP-16289: when the caller supplied return context (currently only Start Recording),
		// navigate in the SAME tab so the editor can hand control back to the originating dialog.
		// The caller persists its in-progress fields on `create-start` first.
		if (props.createReturnTo) {
			emit("create-start");
			router.push({
				path: "/instruction/new",
				query: {
					returnTo: props.createReturnTo,
					returnAction: props.createReturnAction ?? undefined,
					draftKey: props.createDraftKey ?? undefined
				}
			});
			return;
		}
		// Default: open the editor in a new tab so the user doesn't lose their in-progress dialog
		// (Request Stuff, Take Photos, or form builder). Routed through openInternalRoute so the open
		// stays in the user's current context instead of being captured by the installed PWA (LSP-16376).
		openInternalRoute("/instruction/new");
	}

	async function ensureSelectedInstructionLoaded(): Promise<void> {
		// LSP-16289: load() only fetches the first 100 templates ordered by name, so a freshly
		// created template selected on return can be absent from `all`. Fetch it directly and append
		// it when missing so the selection renders without a reload.
		const selectedId = props.modelValue;
		if (!selectedId || all.value.some((row) => row.id === selectedId) || selectedInstructionRequests.has(selectedId)) {
			return;
		}
		selectedInstructionRequests.add(selectedId);
		try {
			const instruction = await api.getInstruction(selectedId);
			if (instruction && !all.value.some((row) => row.id === selectedId)) {
				all.value = [...all.value, instruction];
			}
		} catch (error) {
			console.error("Failed to load the selected Guided Instructions template", error);
			if (isNotFoundError(error) && props.modelValue === selectedId) {
				emit("update:modelValue", null);
			}
		} finally {
			selectedInstructionRequests.delete(selectedId);
		}
	}

	// LSP-20840: preselect the org's default template once the list is loaded. One-shot,
	// null-only (see the prop comment). Each early exit logs why, so "the default didn't show up"
	// is a 10-second triage instead of a reconstruction.
	async function preselectDefaultInstruction(): Promise<void> {
		if (!props.preselectDefault || preselectAttempted) {
			return;
		}
		preselectAttempted = true;
		try {
			await attemptPreselectDefaultInstruction();
		} finally {
			// Applied or not, the attempt is over — the caller downgrades its allow-flag so a
			// remounted picker cannot re-run the preselect within the same dialog lifetime.
			// Except from a stale unmounted instance: its settle belongs to a picker that no
			// longer exists and would wrongly close the gate on its replacement.
			if (!isUnmounted) {
				emit("preselect-settled");
			}
		}
	}

	async function attemptPreselectDefaultInstruction(): Promise<void> {
		if (props.modelValue != null) {
			// Normal no-op: the caller restored a selection (e.g. the LSP-16289 create-and-return
			// handoff) before the list finished loading. Not a fail-condition — stay quiet.
			return;
		}
		let defaultInstruction = all.value.find((row) => row.isDefault === true) ?? null;
		if (!defaultInstruction) {
			// The default can sit beyond the first 100 templates load() fetched — ask for it directly.
			try {
				const response = await api.getInstructions({ page: 1, pageSize: 1, where: { isDefault: true } });
				const candidate = response?.rows?.[0] ?? null;
				// Trust but verify — a row that isn't actually flagged must never be preselected.
				defaultInstruction = candidate?.isDefault === true ? candidate : null;
			} catch (error) {
				console.error("InstructionPicker: failed to look up the default template; not preselecting.", error);
				return;
			}
		}
		if (!defaultInstruction) {
			return;
		}
		if (!all.value.some((row) => row.id === defaultInstruction!.id)) {
			all.value = [...all.value, defaultInstruction];
		}
		// Same unmount rule as preselect-settled: a stale instance's continuation must not push
		// the default into the REPLACEMENT picker, where the user may have already picked something.
		if (props.modelValue == null && !isUnmounted) {
			emit("update:modelValue", defaultInstruction.id);
		}
	}

	async function load(): Promise<void> {
		loading.value = true;
		try {
			const response = await api.getInstructions({ page: 1, pageSize: 100, order: [["name", "ASC NULLS LAST"]] });
			all.value = response.rows ?? [];
		} catch (error) {
			console.error("Failed to load Guided Instructions templates", error);
			all.value = [];
		} finally {
			loading.value = false;
		}
		await ensureSelectedInstructionLoaded();
		await preselectDefaultInstruction();
	}

	onMounted(load);

	watch(
		() => props.modelValue,
		() => {
			void ensureSelectedInstructionLoaded();
		}
	);

	// LSP-20840: consumers enable preselect only after their LSP-16289 draft-restore settles
	// (HomePage always passes a draft key, so the prop can flip true AFTER load() already ran).
	// The one-shot / null-only guards inside preselectDefaultInstruction still apply.
	watch(
		() => props.preselectDefault,
		(enabled) => {
			if (enabled) {
				void preselectDefaultInstruction();
			}
		}
	);
</script>

<style scoped>
	.instruction-picker {
		width: 100%;
	}

	.create-instruction-fixed {
		min-height: 0;
	}

	.create-instruction-btn {
		font-size: 0.875rem;
	}
</style>
